import { FolderDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { arrivedEvent } from './arrived-setup';
import { entriesOf, readDropped } from './dropped-entries';
import { offerDrop, type DroppedPages } from './pending-drop';
import { readOnlyView } from './read-only';
import './page-drop-zone.css';

export interface PageDropZoneProps {
  /** What the outline says while something is dragged over the page. */
  heading: string;
  /** The screen is already bringing pages in: a drop is refused, as a second click would be. */
  disabled: boolean;
  /** Starts the source's own "Add pages"; its picker answers with what was dropped. */
  onPages: (source: DroppedPages['source']) => void;
}

/**
 * Files and folders dragged from the computer onto the workbench, where "Add pages" is offered:
 * the drop feeds the same flow as Choose files or Bring in a folder (a folder goes through the
 * folder flow, with its setup sample), without the browser's "Upload n files to this site?" prompt
 * that `<input webkitdirectory>` brings. Mounted only where the screen accepts pages; while files
 * are over the window it draws an outline over its parent (the start panel or the document spine).
 */
export function PageDropZone({ heading, disabled: busy, onPages }: PageDropZoneProps): ReactNode {
  // A read-only view (read-only.ts) brings nothing in: a drop is refused as a busy screen's is.
  const disabled = busy || readOnlyView();
  const over = useDragOver(disabled);
  const latest = useRef(onPages);
  useEffect(() => {
    latest.current = onPages;
  }, [onPages]);
  useEffect(() => {
    const drop = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      // Always: a file let fall anywhere else would make the browser open it in place of Paige.
      event.preventDefault();
      if (disabled || !event.dataTransfer) return;
      const entries = entriesOf(event.dataTransfer);
      const files = [...event.dataTransfer.files];
      void (entries ? readDropped(entries) : Promise.resolve(loose(files)))
        .catch(() => loose(files))
        .then((pages) => {
          offerDrop(pages);
          latest.current(pages.source);
        });
    };
    window.addEventListener('drop', drop);
    return () => window.removeEventListener('drop', drop);
  }, [disabled]);
  useArrivedSample(disabled, latest);
  if (!over) return null;
  return (
    <div className="page-drop-zone" role="status" aria-live="polite">
      <span className="page-drop-art" aria-hidden="true">
        <FolderDown size={40} strokeWidth={1.25} />
      </span>
      <strong>{heading}</strong>
      <small>PDFs and images. A folder brings its subfolders too.</small>
    </div>
  );
}

/**
 * The documents that arrived for a job with no setup, chosen as its sample (arrived-setup.ts): the same
 * "Add pages" a drop starts; the adapter's picker then answers with them instead of opening a dialog.
 */
function useArrivedSample(
  disabled: boolean,
  latest: RefObject<PageDropZoneProps['onPages']>,
): void {
  useEffect(() => {
    const arrived = () => {
      if (!disabled) latest.current('files');
    };
    window.addEventListener(arrivedEvent, arrived);
    return () => window.removeEventListener(arrivedEvent, arrived);
  }, [disabled, latest]);
}

const loose = (files: File[]): DroppedPages => ({ source: 'files', files });

function carriesFiles(event: DragEvent): boolean {
  return Boolean(event.dataTransfer?.types.includes('Files'));
}

/**
 * Whether files are being dragged over the window. `dragover` repeats while the pointer is over the
 * page, so the outline lasts as long as it does and goes a moment after it stops (left the window,
 * Escape), which `dragleave` alone cannot tell apart from moving between two children.
 */
function useDragOver(disabled: boolean): boolean {
  const [over, setOver] = useState(false);
  useEffect(() => {
    let timer = 0;
    const end = () => {
      window.clearTimeout(timer);
      setOver(false);
    };
    const move = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
      if (disabled) return;
      setOver(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(end, 250);
    };
    window.addEventListener('dragenter', move);
    window.addEventListener('dragover', move);
    window.addEventListener('drop', end);
    window.addEventListener('dragend', end);
    return () => {
      end();
      window.removeEventListener('dragenter', move);
      window.removeEventListener('dragover', move);
      window.removeEventListener('drop', end);
      window.removeEventListener('dragend', end);
    };
  }, [disabled]);
  return over;
}
